/**
 * Ad User Identifier Storage (Cookie-based with Redux fallback)
 *
 * This module manages the user identifier for ad tracking using cookies.
 * Cookies are used for cross-subdomain tracking (e.g., between
 * www.opensolar.com and app.opensolar.com).
 *
 * Format:
 *   - "user_<encrypted>" for known logged-in users (set on login)
 *   - "anon_<uuid>" for anonymous users (auto-generated)
 *
 * If cookies are unavailable, the identifier from Redux state is still
 * returned directly (just not persisted to cookie).
 */

const COOKIE_NAME = 'os_ad_uid'
const COOKIE_EXPIRY_DAYS = 365

/**
 * Get the parent domain for setting cookies (e.g., ".opensolar.com" from "app.opensolar.com")
 * Returns undefined for localhost or IP addresses.
 */
function getParentDomain(): string | undefined {
  const hostname = window.location.hostname

  // Skip for localhost or IP addresses
  if (hostname === 'localhost' || hostname === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return undefined
  }

  // Extract parent domain (e.g., ".opensolar.com" from "app.opensolar.com")
  const parts = hostname.split('.')
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.')
  }
  return undefined
}

/**
 * Get cookie value by name
 */
function getCookie(name: string): string | undefined {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift()
    return cookieValue || undefined
  }
  return undefined
}

/**
 * Set cookie with optional domain
 */
function setCookie(name: string, value: string, days: number, domain?: string): boolean {
  try {
    const expires = new Date()
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)

    let cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
    if (domain) {
      cookie += `; domain=${domain}`
    }

    document.cookie = cookie

    // Verify the cookie was set
    return getCookie(name) === value
  } catch (e) {
    return false
  }
}

/**
 * Check if cookies are supported
 */
function areCookiesSupported(): boolean {
  try {
    document.cookie = 'os_cookie_test=1; SameSite=Strict'
    const supported = document.cookie.indexOf('os_cookie_test') !== -1
    document.cookie = 'os_cookie_test=; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict'
    return supported
  } catch (e) {
    return false
  }
}

/**
 * Get the stored user identifier from cookie.
 * Returns undefined if no identifier is stored.
 */
export function getAdUserIdentifier(): string | undefined {
  return getCookie(COOKIE_NAME)
}

/**
 * Set the user identifier in a cookie on the parent domain.
 * Returns true if stored successfully.
 */
function setAdUserIdentifierInternal(value: string): boolean {
  if (!areCookiesSupported()) {
    return false
  }
  const parentDomain = getParentDomain()
  return setCookie(COOKIE_NAME, value, COOKIE_EXPIRY_DAYS, parentDomain)
}

/**
 * Set the user identifier directly (already formatted).
 * Call this when a user logs in with the user_id_hash from the backend.
 * Format should be "user_<encrypted>" from the backend.
 */
export function setAdUserIdentifier(identifier: string): boolean {
  return setAdUserIdentifierInternal(identifier)
}

/**
 * Get or create an anonymous user identifier.
 * If no identifier exists, generates a new UUID and stores it in a cookie.
 * Returns undefined if cookies are not available.
 */
export function getOrCreateAnonUserIdentifier(): string | undefined {
  const existing = getCookie(COOKIE_NAME)
  if (existing) {
    return existing
  }

  if (!areCookiesSupported()) {
    return undefined
  }

  // Generate new anon identifier
  const uuid = crypto.randomUUID()
  const identifier = `anon_${uuid}`

  if (setAdUserIdentifierInternal(identifier)) {
    return identifier
  }

  return undefined
}

/**
 * Get the current user identifier for ad tracking requests.
 *
 * Priority:
 * 1. userIdHash from Redux state (if provided) — idempotently syncs to cookie
 * 2. Existing cookie value
 * 3. Create new anonymous identifier
 *
 * @param userIdHash - The user_id_hash from Redux auth state (format: "user_<encrypted>").
 *                     Pass undefined/null if user is not logged in.
 */
export function getUserIdentifier(userIdHash?: string | null): string | undefined {
  if (userIdHash) {
    // Idempotently sync to cookie so embed/cross-domain flows also benefit
    if (getCookie(COOKIE_NAME) !== userIdHash) {
      setAdUserIdentifierInternal(userIdHash)
    }
    return userIdHash
  }

  // No Redux state available — fall back to cookie then anon creation
  return getAdUserIdentifier() || getOrCreateAnonUserIdentifier()
}

export default {
  getAdUserIdentifier,
  setAdUserIdentifier,
  getOrCreateAnonUserIdentifier,
  getUserIdentifier,
}
